import { Fragment, useEffect, useState, useContext, useRef } from 'react';
import { Row, Grid, Col, Table, Button, IconButton, toaster, Message, ButtonGroup, Whisper, Tooltip, ButtonToolbar, Loader, Text, Badge, Modal, Dropdown } from 'rsuite';
import { useApolloClient, gql, useQuery } from '@apollo/client';
import startCase from 'lodash/startCase';
import { ApplicationContext, ResponsiveTable } from 'shared';
import { v4 as uuidv4 } from 'uuid';
import { COLOR, FORMAT, FORMAT_SHORT_DATE, hasStripeIntegration, setHeaderTitle } from 'lib/env';
import ClientRepresentativeSignature from 'components/document/ClientRepresentativeSignature';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import { queryDocument } from 'gql/documents';
import { useGetToken } from 'lib/auth';
import { useMediaQuery } from 'react-responsive';
import { format, parseISO } from 'date-fns';
import Icon from '@rsuite/icons/lib/Icon';
import { useViewport } from 'shared/ViewportProvider';
import { loadStripe } from '@stripe/stripe-js';
import { useApi } from 'lib';
import { queryCustomer } from 'gql/customer';
import { CardElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { MdMoreHoriz, MdMoreVert } from 'react-icons/md';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const PortalProfileView = () => {
  const api = useApi();
  const client = useApolloClient();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });
  const { showError, showMessage, showConfirmation } = useContext(ApplicationContext);
  const elements = useElements();
  const { state } = useViewport();
  const [document, setDocument] = useState<any>(undefined);
  const [documentSigning, setDocumentSigning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const token: any = useGetToken(true);
  const stripe: any = useStripe();
  const [contacts, setContacts] = useState<Array<any>>([{ guid: uuidv4() }]);
  const customer = useQuery(queryCustomer, { variables: { guid: token.data.resource } });
  const [savingCard, setSavingCard] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const tokenIsExpired = (token as any).exp < new Date().getTime() / 1000;

  const IS_ASSINIBOINE = token.data.tenant === 'assiniboine' || token.data.tenant === 'greyrock';
  const IS_GREYROCK = token.data.tenant === 'greyrock';
  const IS_BUGABOO = token.data.tenant === 'bugaboo' || token.data.tenant === 'snowpatch';
  const IS_KALADI_KITCHENS = token.data.tenant === 'kaladikitchens';
  const IS_KALADI_PROPERTIES = token.data.tenant === 'kaladiproperties';


  const handleCreateStripeCard = async () => {
    const customerId = token.sub;

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setSavingCard(true);
    const cardElement = elements.getElement('card');
    let response: any = {};

    try {
      response = await client.mutate({
        mutation: gql`mutation upsertStripeCustomer($id: ID!) {
          upsertStripeCustomer(id: $id) {
            success
            code
            message
            result
          }
        }`, variables: { id: customerId }
      });
    } catch (err: any) {
      toaster.push(<Message type='error' showIcon closable>{err.message}</Message>);
      return;
    }

    if (cardElement) {
      try {
        const result = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          metadata: {
            notes: 'Customer provided'
          }
        });


        if (result.error) {
          toaster.push(<Message type='error' showIcon closable>{result.error.message}</Message>);
        } else {
          const binding: any = await client.mutate({
            mutation: gql`mutation attachStripePayment($paymentId: String!, $customerId: ID!, $stripeCustomerId: String!, $isDefault: Boolean, $source: String) {
              attachStripePayment(paymentId: $paymentId, customerId: $customerId, stripeCustomerId: $stripeCustomerId, isDefault: $isDefault, source: $source) {
                success
                code
                message
                result
              }
            }`, variables: {
              paymentId: result.paymentMethod.id,
              customerId: customerId,
              stripeCustomerId: response.data.upsertStripeCustomer.result.stripeCustomerId,
              isDefault: true,
              source: 'portal'
            }
          });

          toaster.push(<Message type={binding.data.attachStripePayment.success ? 'success' : 'error'} showIcon closable>{binding.data.attachStripePayment.message}</Message>);

        }
      } catch (err: any) {
        toaster.push(<Message type='error' showIcon closable>{err.message}</Message>)
      } finally {
        setSavingCard(false);
        setShowAddCard(false);
        customer?.refetch();
      }
    }
  }

  const handleStripeDeletePayment = async (customerGuid: string, paymentId: string) => {
    const response: any = await client.mutate({
      mutation: gql`mutation deleteStripePayment($customerGuid: ID!, $paymentId: String!) {
        deleteStripePayment(customerGuid: $customerGuid, paymentId: $paymentId) {
          success
          code
          message
          result
        }
      }`, variables: { customerGuid: customerGuid, paymentId: paymentId }
    });

    toaster.push(<Message type={response.data.deleteStripePayment.success ? 'success' : 'error'} showIcon closable>{response.data.deleteStripePayment.message}</Message>);
    customer?.refetch();
  }

  const handleStripeUpdatePrimaryPayment = async (customerGuid: string, paymentId: string) => {
    const response: any = await client.mutate({
      mutation: gql`mutation updateStripePrimaryPayment($customerGuid: ID!, $paymentId: String!) {
        updateStripePrimaryPayment(customerGuid: $customerGuid, paymentId: $paymentId) {
          success
          code
          message
          result
        }
      }`, variables: { customerGuid: customerGuid, paymentId: paymentId }
    });

    toaster.push(<Message type={response.data.updateStripePrimaryPayment.success ? 'success' : 'error'} showIcon closable>{response.data.updateStripePrimaryPayment.message}</Message>);
    customer?.refetch();
  }

  return (
    <div className="App">
      {(tokenIsExpired || (!customer?.loading && !customer?.data?.customer))
        ? <Fragment>
          <Grid>
            <Row>
              <Col xs={24}>
                <br />
                <Message type="error">This link has expired, please contact our office to receive a new link.</Message>
              </Col>
            </Row>
          </Grid>
        </Fragment>
        : <Fragment>
          {customer?.loading
            ? <Loader content="Loading..." />
            : <>
              {customer?.data?.customer
                ? <Grid fluid className='p-0'>
                  <Row>
                    <Col xs={18} md={12}>
                      <MobileView>
                        <h4>{customer?.data?.customer?.displayName}</h4>
                      </MobileView>
                      <BrowserView>
                        <h3>{customer?.data?.customer?.displayName}</h3>
                      </BrowserView>
                      {customer?.data?.customer?.workAddress && <div>
                        <p><strong>Work Address</strong></p>
                        <div>{[customer?.data?.customer?.workAddress, customer?.data?.customer?.workCity, customer?.data?.customer?.workProvince,
                        customer?.data?.customer?.workPostalCode].filter((v: any) => (v || '').length).join(', ')}</div>
                      </div>}

                      {customer?.data?.customer?.customerAddress && <div>
                        <p><strong>{customer?.data?.customer?.workAddress ? 'Billing' : 'Site'} Address</strong></p>
                        <div>{[customer?.data?.customer?.customerAddress, customer?.data?.customer?.city, customer?.data?.customer?.province,
                        customer?.data?.customer?.postalCode].filter((v: any) => (v || '').length).join(', ')}</div>
                      </div>}
                      <br />
                    </Col>
                    <Col xs={4} md={12} className='text-right'>
                      <MobileView>
                        <span>Profile</span>
                      </MobileView>
                      <BrowserView>
                        <h3>Profile</h3>
                      </BrowserView>
                    </Col>
                  </Row>
                  {hasStripeIntegration(state.companies) &&
                    <Row>
                      <Col xs={24} md={16}>
                        <fieldset>
                          <legend>Payment Methods</legend>

                          {customer?.data?.customer?.billing
                            ? <Grid fluid>
                              {(customer?.data?.customer?.billing || []).map((b: any) => <Row key={b.guid}>
                                <Col md={8}>
                                  <BrowserView>
                                    <Text size={'lg'}>Card Last 4 Digits: {b.maskedPan}</Text>
                                  </BrowserView>
                                  <MobileView>
                                    <span>Card Last 4 Digits: {b.maskedPan}</span>
                                  </MobileView>
                                </Col>
                                <Col md={4} className='text-center'>
                                  <span> </span>
                                  <BrowserView>
                                    <>
                                      {b.isDefault && <Badge color='blue' content={<Text size='sm' style={{ color: '#fff' }}>Default</Text>} />}
                                    </>
                                  </BrowserView>
                                  <MobileView>
                                    <>
                                      {b.isDefault && <Badge color='blue' content="Default" />}
                                    </>
                                  </MobileView>
                                </Col>
                                <Col md={6}>
                                  <BrowserView>
                                    <Text size={'lg'}>Expires: {b.expYear}/{b.expMonth}</Text>
                                  </BrowserView>
                                  <MobileView>
                                    <span>Expires: {b.expYear}/{b.expMonth}</span>
                                  </MobileView>
                                </Col>
                                <Col md={2}>
                                  <Dropdown renderToggle={(props: any, ref: any) => <button {...props} ref={ref}><MdMoreHoriz /></button>} size='xs' title='' noCaret>
                                    {!b.isDefault && <Dropdown.Item onClick={() => handleStripeUpdatePrimaryPayment(customer?.data?.customer?.guid, b.dataKey)}>Make Default</Dropdown.Item>}
                                    <Dropdown.Item onClick={() =>
                                      showConfirmation(`This will permanently delete your payment method, ${b.maskedPan}?`, 'Delete Payment Method', () => {
                                        // if (hasMonerisIntegration(state.companies)) {
                                        // handleMonerisDeletePayment(cust.id, c.dataKey);
                                        // } else if (hasStripeIntegration(state.companies)) {
                                        handleStripeDeletePayment(customer?.data?.customer?.guid, b.dataKey);
                                        // }
                                      })
                                    }><span className='text-danger'>Delete</span></Dropdown.Item>
                                  </Dropdown>
                                </Col>
                              </Row>
                              )}
                            </Grid>
                            : <Message type='info'>No payment methods defined</Message>
                          }
                          <div className='mt-12'>
                            <Button size="sm" onClick={() => setShowAddCard(true)} appearance='primary'>Add Payment Method</Button>
                          </div>
                        </fieldset>
                      </Col>
                    </Row>
                  }
                </Grid>
                : <Message type='error'>Profile not found. Please contact our office for new link.</Message>
              }
            </>
          }

          <ClientRepresentativeSignature
            signatureType={IS_KALADI_PROPERTIES ? 'tenant' : 'client'}
            show={documentSigning}
            document={document}
            onHide={() => {
              setDocumentSigning(false);
            }}
            onApproveDocument={(result: any) => {
              setDocument(result);
              setDocumentSigning(false);
            }}
          />
        </Fragment>
      }

      <Modal open={showAddCard}>
        <Modal.Title>
          Add Payment Method
        </Modal.Title>
        <Modal.Body>
          <CardElement options={{ hidePostalCode: true }} />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowAddCard(false)} size='sm' appearance='subtle' disabled={savingCard}>Cancel</Button>
          <Button onClick={handleCreateStripeCard} size='sm' appearance='primary' loading={savingCard} disabled={savingCard}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div >
  );
}

export default PortalProfileView;
